import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { useTranslation } from "react-i18next";
import { getUserById } from "../../api/User/userApi";
import { mapUserToPermissionState } from "../../utils/userPermissionsUtils";
import { useProveedorPermissions } from "../../hooks/useProveedorPermissions";
import HelpItem from "./HelpItem";
import {
  HELP_SECTIONS,
  canAccessHelpItem,
  findHelpItem,
  findHelpMatch,
  getHelpItemCopy,
  itemMatchesSearch,
} from "./helpCatalog";

const DRAWER_WIDTH = 560;

const DrawerInstrucciones = ({ open, onClose }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { hasProveedorLimit } = useProveedorPermissions();
  const isPage = open === undefined;
  const drawerOpen = isPage ? true : open;

  const [search, setSearch] = useState("");
  const [expandedSection, setExpandedSection] = useState("start");
  const [expandedItem, setExpandedItem] = useState("whatIs");
  const [permissions, setPermissions] = useState({});
  const [deniedModule, setDeniedModule] = useState(null);
  const itemRefs = useRef({});

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const token = localStorage.getItem("token");
      if (!token) {
        if (!cancelled) setPermissions({});
        return;
      }
      try {
        const decoded = jwtDecode(token);
        const userId = decoded?.userId;
        if (!userId) {
          if (!cancelled) setPermissions({});
          return;
        }
        const user = await getUserById(userId);
        if (!cancelled) setPermissions(mapUserToPermissionState(user));
      } catch (err) {
        console.error("Help Center: error cargando permisos", err);
        if (!cancelled) setPermissions({});
      }
    };
    if (drawerOpen) load();
    return () => {
      cancelled = true;
    };
  }, [drawerOpen]);

  useEffect(() => {
    if (!drawerOpen) return;
    setSearch("");
    setDeniedModule(null);
    const match = findHelpMatch(pathname);
    setExpandedSection(match.sectionId);
    setExpandedItem(match.itemId);
  }, [drawerOpen, pathname]);

  const query = search.trim().toLowerCase();

  const visibleSections = useMemo(() => {
    return HELP_SECTIONS.map((section) => ({
      ...section,
      items: section.items.filter((item) => itemMatchesSearch(t, item, query)),
    })).filter((section) => section.items.length > 0);
  }, [query, t]);

  const openItem = (itemId) => {
    const found = findHelpItem(itemId);
    if (!found) return;
    setSearch("");
    setExpandedSection(found.section.id);
    setExpandedItem(itemId);
    window.setTimeout(() => {
      itemRefs.current[itemId]?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }, 80);
  };

  const goToModule = (item) => {
    if (!item?.route) return;
    const allowed = canAccessHelpItem(item, permissions, { hasProveedorLimit });
    if (!allowed) {
      const copy = getHelpItemCopy(t, item.id);
      setDeniedModule(copy.title || item.id);
      return;
    }
    navigate(item.route);
    onClose?.();
  };

  const here = findHelpMatch(pathname);

  return (
    <>
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={onClose}
        variant={isPage ? "permanent" : "temporary"}
        sx={{
          "& .MuiDrawer-paper": {
            width: { xs: "100%", sm: DRAWER_WIDTH },
            maxWidth: "100%",
            bgcolor: "background.paper",
            backgroundImage: "none",
            borderLeft: "1px solid",
            borderColor: "divider",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: 2,
            pt: 2,
            pb: 0.5,
          }}
        >
          <Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 800,
                color: "primary.main",
                letterSpacing: 0.2,
              }}
            >
              {t("help.title")}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t("help.subtitle")}
            </Typography>
          </Box>
          {onClose ? (
            <Tooltip title={t("common.close")}>
              <IconButton onClick={onClose} aria-label={t("common.close")}>
                <CloseIcon />
              </IconButton>
            </Tooltip>
          ) : null}
        </Box>

        <Divider sx={{ my: 1 }} />

        <Box sx={{ px: 2, pb: 1 }}>
          <TextField
            fullWidth
            size="small"
            placeholder={t("help.searchPlaceholder")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "text.disabled" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                bgcolor: alpha(theme.palette.background.default, 0.6),
              },
            }}
          />
        </Box>

        <Box
          sx={{
            px: 2,
            pb: 3,
            overflowY: "auto",
            height: "calc(100vh - 148px)",
          }}
        >
          {visibleSections.length === 0 ? (
            <Typography
              variant="body2"
              sx={{ textAlign: "center", mt: 4, color: "text.secondary" }}
            >
              {t("help.noResults", { search })}
            </Typography>
          ) : (
            visibleSections.map((section) => {
              const Icon = section.icon;
              const sectionOpen = query ? true : expandedSection === section.id;
              return (
                <Accordion
                  key={section.id}
                  disableGutters
                  elevation={0}
                  expanded={sectionOpen}
                  onChange={(_, next) => {
                    if (query) return;
                    setExpandedSection(next ? section.id : false);
                  }}
                  sx={{
                    mb: 1,
                    bgcolor: "transparent",
                    "&:before": { display: "none" },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      px: 0.5,
                      minHeight: 44,
                      "& .MuiAccordionSummary-content": {
                        my: 0.5,
                        alignItems: "center",
                      },
                    }}
                  >
                    <Icon sx={{ color: "primary.main", mr: 1, fontSize: 22 }} />
                    <Typography sx={{ fontWeight: 800 }}>
                      {t(`help.sections.${section.id}`)}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ px: 0, pt: 0 }}>
                    {section.items.map((item) => (
                      <Box
                        key={item.id}
                        ref={(el) => {
                          itemRefs.current[item.id] = el;
                        }}
                      >
                        <HelpItem
                          item={item}
                          expanded={query ? true : expandedItem === item.id}
                          onToggle={(_, next) => {
                            if (query) return;
                            setExpandedItem(next ? item.id : false);
                            setExpandedSection(section.id);
                          }}
                          isHere={here.itemId === item.id}
                          canAccess={canAccessHelpItem(item, permissions, {
                            hasProveedorLimit,
                          })}
                          onGoTo={goToModule}
                          onOpenRelated={openItem}
                        />
                      </Box>
                    ))}
                  </AccordionDetails>
                </Accordion>
              );
            })
          )}
        </Box>
      </Drawer>

      <Dialog
        open={Boolean(deniedModule)}
        onClose={() => setDeniedModule(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <LockOutlinedIcon color="warning" />
          {t("help.noAccessTitle")}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {t("help.noAccessBody", { module: deniedModule })}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="contained"
            onClick={() => setDeniedModule(null)}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            {t("help.noAccessOk")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DrawerInstrucciones;
